import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  ButtonGroup,
  Autocomplete,
  FormControl,
  Fade,
} from "@mui/material";
import Swal from "sweetalert2";

import api from "../../../api/axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../../Components/helpers/Helpers";

import {
  useRichTextEditor,
  RichTextEditor,
} from "../../../Mensajes/components/Editor";
import LoadingPage from "../../../Components/Common/Loading/LoadingPage";

const Editar = () => {
  const navigate = useNavigate();
  const { goPanel, goTable, buildTableUrl } = useLocationHelpers();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  /* ===========================
      RICH TEXT EDITOR
  ============================ */

  const {
    editorState,
    onEditorChange,
    clearEditor,
    getHtmlFromEditor,
    loadHtmlToEditor,
  } = useRichTextEditor();

  /* ===========================
      STATE
  ============================ */

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const [options, setOptions] = useState({
    companies: [],
  });

  const [contractData, setContractData] = useState({
    id: 0,
    company_id: null,
    contract_title: "",
    contract: "",
    contract_contatcs: "",
    contract_price: "",
    contract_percentage: "",
    contract_files: "",
  });

  /* ===========================
      FETCH INITIAL DATA
  ============================ */

  const getInitialData = async () => {
    try {
      setLoadingOptions(true);
      setLoadingData(true);

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/contracts/edit`,
        { id },
      );

      console.log(data);

      const companies = [
        { company_name: "Seleccionar", id: 0 },
        ...data.companies,
      ];

      const contract = data.contract;

      const selectedCompany =
        companies.find((c) => c.id === contract.company_id) || null;

      setOptions({ companies });

      setContractData({
        id: contract.id,
        company_id: selectedCompany,
        contract_title: contract.contract_title || "",
        contract: contract.contract || "",
        contract_contatcs: contract.contract_contatcs || "",
        contract_price: contract.contract_price || "",
        contract_percentage: contract.contract_percentage || "",
        contract_files: contract.contract_files || "",
      });

      if (contract.contract) {
        loadHtmlToEditor(contract.contract);
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudieron cargar los datos", "error");
    } finally {
      setLoadingOptions(false);
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (id) getInitialData();
  }, [id]);

  /* ===========================
      HANDLERS
  ============================ */

  const handleChange = ({ target }) => {
    setContractData((prev) => ({
      ...prev,
      [target.name]: target.value.trim(),
    }));
  };

  const handleAutocompleteChange = (_, value, fieldName) => {
    setContractData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  /* ===========================
      VALIDATION
  ============================ */

  const validateForm = () => {
    const errors = [];

    if (!contractData.company_id || contractData.company_id.id === 0) {
      errors.push("Empresa");
    }

    if (!contractData.contract_title.trim()) {
      errors.push("Hoja de encargo/Contrato");
    }

    const html = getHtmlFromEditor();
    const isEmpty = !html || html.replace(/<[^>]+>/g, "").trim() === "";

    if (isEmpty) {
      errors.push("Observaciones");
    }

    if (errors.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Campos obligatorios",
        html: `
        <div style="text-align:left">
          Complete los siguientes campos:
          <ul>
            ${errors.map((e) => `<li>${e}</li>`).join("")}
          </ul>
        </div>
      `,
        confirmButtonColor: "#1ab394",
      });

      return false;
    }

    return true;
  };

  const buildPayload = () => {
    return {
      id: contractData.id,
      company_id: contractData.company_id?.id || 0,
      contract_title: contractData.contract_title,
      contract: getHtmlFromEditor(),
      contract_contatcs: contractData.contract_contatcs,
      contract_price: contractData.contract_price,
      contract_percentage: contractData.contract_percentage,
      contract_files: contractData.contract_files,
    };
  };

  /* ===========================
      SUBMIT
  ============================ */

  const handleContractsFormInformation = async () => {
    if (!validateForm()) return;

    const confirm = await Swal.fire({
      title: "¿Desea guardar los cambios?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Actualizar",
      cancelButtonText: "Volver",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      Swal.fire({
        title: "Actualizando Hoja de encargo/Contrato",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await api.post(
        `${process.env.REACT_APP_API_URL}/contracts/update`,
        buildPayload(),
      );

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Hoja de encargo/Contrato actualizado",
        confirmButtonColor: "#1ab394",
      });

      navigate(`/contratos/ver?id=${contractData.id}`);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo actualizar la información", "error");
    }
  };

  /* ===========================
      RENDER
  ============================ */

  return (
    <div className="fatherContainer">
      <CommonSubHeader
        title="Hojas de encargo - Contratos"
        subTitle="Editar"
        specialRute="contratos"
      />
      {loadingData && <LoadingPage isLoading />}
      {!loadingData && (
        <Fade in timeout={800}>
          <Box component="form" className="formMainContainer" noValidate>
            <Grid container p={2}>
              <Grid item xs={12} sm={6} p={1}>
                <Autocomplete
                  value={contractData.company_id}
                  options={options.companies}
                  loading={loadingOptions}
                  size="small"
                  getOptionLabel={(o) => o?.company_name || ""}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  onChange={(e, v) =>
                    handleAutocompleteChange(e, v, "company_id")
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Empresa" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} p={1}>
                <TextField
                  name="contract_title"
                  label="Hoja de encargo/Contrato"
                  value={contractData.contract_title}
                  onChange={handleChange}
                  size="small"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} p={1}>
                <FormControl fullWidth>
                  <FormLabel sx={{ my: 2 }}>Observaciones</FormLabel>
                  <RichTextEditor
                    editorState={editorState}
                    onEditorChange={onEditorChange}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} mt={2}>
                <ButtonGroup fullWidth>
                  <Button
                    color="success"
                    onClick={handleContractsFormInformation}
                    disabled={loadingData}>
                    Actualizar
                  </Button>
                  <NavButton
                    color="error"
                    to={buildTableUrl("contratos")}
                    onClick={() => goTable("contratos")}>
                    Tabla
                  </NavButton>
                  <Button onClick={goPanel}>Inicio</Button>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}
    </div>
  );
};

export default Editar;
